import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Compass, Link2, Loader2, UserPlus } from '@/ui/icons'
import { Button } from '@/ui/components'
import { useAuth } from '@/modules/auth'
import { useTripInviteFriends } from '@/modules/trips/hooks/useTripInviteFriends'
import { formatTripDateRange } from '@/modules/trips/utils/dateHelpers'
import { ShareLinksApi, permissionPresets } from '@/shared/api/shareLinks'
import type { RangerBuiltTrip } from '@/shared/api/ranger'
import type { TripFriendItem } from '@/shared/api/trips'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import { cn } from '@/shared/utils'

function friendName(friend: TripFriendItem): string {
  return friend.full_name?.trim() || friend.username?.trim() || friend.email || 'Crew member'
}

function friendVehicle(friend: TripFriendItem): string | null {
  const vehicle = friend.primary_vehicle
  if (!vehicle) return null
  return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
}

function FriendRow({
  friend,
  selected,
  disabled,
  onToggle,
}: {
  friend: TripFriendItem
  selected: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const name = friendName(friend)
  const vehicle = friendVehicle(friend)
  const isPending = friend.invite_status === 'pending'

  return (
    <li>
      <label
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 transition-colors',
          isPending ? 'cursor-default' : 'cursor-pointer hover:bg-earth-100 has-[:focus-visible]:bg-earth-100'
        )}
      >
        <input
          type="checkbox"
          checked={selected || isPending}
          disabled={isPending || disabled}
          onChange={onToggle}
          className="h-4 w-4 shrink-0 rounded border-earth-300 accent-adventure-600 disabled:opacity-50"
        />
        {friend.profile_image_path ? (
          <img src={getUserProfileUrl(friend.profile_image_path)} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            aria-hidden
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-earth-100 text-xs font-medium text-earth-700"
          >
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-foreground">{name}</span>
          {vehicle && <span className="block truncate text-xs text-earth-600">{vehicle}</span>}
        </span>
        {isPending && <span className="shrink-0 text-xs text-earth-600">Invited</span>}
      </label>
    </li>
  )
}

function CopyInviteLinkButton({ tripId, emphasized }: { tripId: string; emphasized: boolean }) {
  const { getAccessToken } = useAuth()
  const [state, setState] = useState<'idle' | 'working' | 'copied' | 'error'>('idle')
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null)

  const handleCopy = async () => {
    setState('working')
    setFallbackUrl(null)
    let url: string
    try {
      const api = new ShareLinksApi(getAccessToken)
      const links = await api.getShareLinks(tripId)
      const reusable = links.find((l) => l.allow_join && (!l.expires_at || new Date(l.expires_at) > new Date()))
      const link = reusable ?? (await api.createShareLink(tripId, { permissions: permissionPresets.basicInfo, allow_join: true }))
      url = `${window.location.origin}/share/${link.token}`
    } catch {
      setState('error')
      return
    }

    try {
      await navigator.clipboard.writeText(url)
      setState('copied')
      setTimeout(() => setState((current) => (current === 'copied' ? 'idle' : current)), 2000)
    } catch {
      // Clipboard blocked: the link is still good, so show it to copy by hand.
      setFallbackUrl(url)
      setState('idle')
    }
  }

  return (
    <div>
      <Button
        type="button"
        variant={emphasized ? 'default' : 'ghost'}
        size="sm"
        onClick={handleCopy}
        disabled={state === 'working'}
        className="h-8 w-full gap-1.5 text-xs"
      >
        {state === 'working' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {state === 'copied' && <Check className="h-3.5 w-3.5" />}
        {(state === 'idle' || state === 'error') && <Link2 className="h-3.5 w-3.5" />}
        {state === 'copied' ? 'Link copied' : state === 'error' ? 'Try the link again' : 'Copy invite link'}
      </Button>
      {state === 'error' && (
        <p role="alert" className="mt-1 text-center text-xs text-error">
          Couldn't get an invite link.
        </p>
      )}
      {fallbackUrl && (
        <input
          readOnly
          value={fallbackUrl}
          aria-label="Invite link"
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
        />
      )}
    </div>
  )
}

/**
 * Shown when Ranger creates a trip: confirms what was created, then gets people on it.
 * Names the user already gave Ranger arrive ticked, and nothing sends until they confirm.
 */
export function RangerBuildTripCard({ trip }: { trip: RangerBuiltTrip }) {
  const { friendsQuery, sendInvites } = useTripInviteFriends(trip.id)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(trip.suggested_friend_ids))

  const friends = useMemo(() => friendsQuery.data ?? [], [friendsQuery.data])
  const invitableSelected = friends.filter((f) => selectedIds.has(f.id) && f.invite_status !== 'pending')
  const results = sendInvites.data
  const failedCount = results?.filter((r) => r.status === 'failed' || r.status === 'not_found').length ?? 0
  const sentCount = results?.filter((r) => r.status === 'invited').length ?? 0
  const dateRange = formatTripDateRange(trip.start_date, trip.end_date)
  const details = [dateRange, trip.destination].filter(Boolean).join(' · ')

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSend = () => {
    sendInvites.mutate(
      invitableSelected.map((f) => f.id),
      { onSuccess: () => setSelectedIds(new Set()) }
    )
  }

  return (
    <div className="w-full overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex items-start gap-2.5 border-b border-border bg-earth-50 px-3 py-2.5">
        <Compass className="mt-0.5 h-4 w-4 shrink-0 text-adventure-600" aria-hidden />
        <div className="min-w-0 flex-1">
          <Link
            to={`/trips/${trip.id}`}
            className="block truncate text-sm font-medium text-foreground hover:underline focus:outline-none focus-visible:underline"
          >
            {trip.name}
          </Link>
          {details && <p className="truncate text-xs text-earth-600">{details}</p>}
          {trip.description && <p className="mt-1.5 text-xs leading-relaxed text-earth-700">{trip.description}</p>}
        </div>
      </div>

      {friendsQuery.isLoading && (
        <ul aria-label="Loading friends" className="animate-pulse motion-reduce:animate-none">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-center gap-2.5 px-3 py-2.5">
              <span className="h-7 w-7 shrink-0 rounded-full bg-earth-100" />
              <span className="h-3 flex-1 rounded bg-earth-100" />
            </li>
          ))}
        </ul>
      )}

      {friendsQuery.isError && (
        <div className="px-3 py-3 text-xs text-earth-700">
          <p role="alert">Couldn't load your friends.</p>
          <button
            type="button"
            onClick={() => friendsQuery.refetch()}
            className="mt-1 font-medium text-adventure-700 hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {friendsQuery.isSuccess && friends.length === 0 && (
        <p className="px-3 py-3 text-xs text-earth-700">
          Share the link to get your crew on this trip. Anyone you travel with shows up here next time.
        </p>
      )}

      {friends.length > 0 && (
        <>
          <p className="px-3 pt-2.5 text-xs font-medium text-earth-800">Who's coming?</p>
          <ul className="mt-1 max-h-56 overflow-y-auto overscroll-contain">
            {friends.map((friend) => (
              <FriendRow
                key={friend.id}
                friend={friend}
                selected={selectedIds.has(friend.id)}
                disabled={sendInvites.isPending}
                onToggle={() => toggle(friend.id)}
              />
            ))}
          </ul>
        </>
      )}

      <div className="flex flex-col gap-1 border-t border-border p-2">
        {friends.length > 0 && (
          <Button
            type="button"
            size="sm"
            onClick={handleSend}
            disabled={invitableSelected.length === 0 || sendInvites.isPending}
            className="h-8 w-full gap-1.5 text-xs"
          >
            {sendInvites.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            {invitableSelected.length > 0
              ? `Invite ${invitableSelected.length} ${invitableSelected.length === 1 ? 'friend' : 'friends'}`
              : 'Choose friends to invite'}
          </Button>
        )}
        {(sendInvites.isError || failedCount > 0) && (
          <p role="alert" className="text-center text-xs text-error">
            {sendInvites.isError
              ? "Those invites didn't go through. Try again."
              : `${failedCount} ${failedCount === 1 ? "invite didn't" : "invites didn't"} go through.`}
          </p>
        )}
        {sentCount > 0 && failedCount === 0 && !sendInvites.isPending && (
          <p role="status" className="text-center text-xs text-earth-700">
            {sentCount === 1 ? 'Invite sent.' : `${sentCount} invites sent.`}
          </p>
        )}
        <CopyInviteLinkButton tripId={trip.id} emphasized={friendsQuery.isSuccess && friends.length === 0} />
      </div>
    </div>
  )
}
